import axios from 'axios';

const API_BASE_URL = '/api'; // Usar proxy do Vite

// Configurar axios com interceptors para tratamento de erros
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // ✅ Enviar cookies automaticamente
  headers: {
    'Content-Type': 'application/json',
  }
});

// ✅ SEGURANÇA: Tokens agora em cookies httpOnly
// Navegador envia automaticamente, não precisa adicionar manualmente
api.interceptors.request.use(
  (config) => {
    // Cookies são enviados automaticamente pelo navegador
    // Não precisa mais adicionar Authorization header manualmente
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    if (error.response?.status === 404) {
      throw new Error('Recurso não encontrado');
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data?.error || 'Dados inválidos');
    } else if (error.response?.status >= 500) {
      throw new Error('Erro interno do servidor');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Timeout na requisição');
    } else if (!error.response) {
      throw new Error('Erro de conexão com o servidor');
    }
    
    throw error;
  }
);

export class Partnership {
  /**
   * Lista parcerias com paginação server-side
   * @param {string} sort - Campo de ordenação (ex: '-created_at')
   * @param {object} filters - Filtros de busca
   * @param {number} page - Número da página (1-indexed)
   * @param {number} limit - Itens por página
   * @returns {Promise<{data: Array, total: number, page: number, totalPages: number}>}
   */
  static async list(sort = '-created_at', filters = {}, page = 1, limit = 10) {
    try {
      const params = {
        sort,
        page,
        limit,
        ...filters
      };

      const response = await api.get('/partnerships', { params });
      
      // Retornar dados paginados com metadados
      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        page: response.data.page || page,
        totalPages: response.data.totalPages || 1
      };
    } catch (error) {
      console.error('Error listing partnerships:', error);
      throw error;
    }
  }

  /**
   * Lista TODAS as parcerias sem paginação (para filtros dinâmicos)
   * @param {string} sort - Campo de ordenação
   * @param {object} filters - Filtros de busca
   * @returns {Promise<Array>}
   */
  static async listAll(sort = '-created_at', filters = {}) {
    try {
      const params = {
        sort,
        limit: 10000, // Limite alto para buscar todos
        ...filters
      };

      const response = await api.get('/partnerships', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Error listing all partnerships:', error);
      throw error;
    }
  }

  static async findById(id) {
    try {
      const response = await api.get(`/partnerships/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error finding partnership:', error);
      throw error;
    }
  }

  static async create(data) {
    try {
      const response = await api.post('/partnerships', data);
      return response.data;
    } catch (error) {
      console.error('Error creating partnership:', error);
      throw error;
    }
  }

  static async update(id, data) {
    try {
      const response = await api.put(`/partnerships/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating partnership:', error);
      throw error;
    }
  }

  static async delete(id) {
    try {
      const response = await api.delete(`/partnerships/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting partnership:', error);
      throw error;
    }
  }

  static async bulkCreate(partnerships) {
    try {
      const response = await api.post('/partnerships/bulk', { partnerships });
      return response.data;
    } catch (error) {
      console.error('Error bulk creating partnerships:', error);
      throw error;
    }
  }

  static async recalculateAllStatuses() {
    try {
      const response = await api.post('/partnerships/recalculate-statuses');
      return response.data;
    } catch (error) {
      console.error('Error recalculating statuses:', error);
      throw error;
    }
  }

  static async getStats() {
    try {
      const response = await api.get('/partnerships/stats');
      return response.data;
    } catch (error) {
      console.error('Error getting stats:', error);
      throw error;
    }
  }

  static async getOscReport(cnpj) {
    try {
      const response = await api.get(`/partnerships/osc/${encodeURIComponent(cnpj)}`);
      return response.data;
    } catch (error) {
      console.error('Error getting OSC report:', error);
      throw error;
    }
  }

  /**
   * =====================================================
   * METADADOS DINÂMICOS
   * Busca valores distintos para selects dinâmicos
   * =====================================================
   */
  static async getTiposParceria() {
    try {
      const response = await api.get('/partnerships/metadata/tipos-parceria');
      return response.data.data || [];
    } catch (error) {
      console.error('Error getting tipos parceria:', error);
      throw error;
    }
  }

  static async getOrigens() {
    try {
      const response = await api.get('/partnerships/metadata/origens');
      return response.data.data || [];
    } catch (error) {
      console.error('Error getting origens:', error);
      throw error;
    }
  }

  static async getPrazos() {
    try {
      const response = await api.get('/partnerships/metadata/prazos');
      return response.data.data || [];
    } catch (error) {
      console.error('Error getting prazos:', error);
      throw error;
    }
  }

  /**
   * Exportar parcerias em formato específico
   * @param {string} format - Formato: 'excel', 'csv' ou 'txt'
   * @param {object} filters - Filtros aplicados
   */
  static async export(format, filters = {}) {
    try {
      const params = {};
      
      if (filters.search) params.search = filters.search;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.year && filters.year !== 'all') params.year = filters.year;
      if (filters.tipo && filters.tipo !== 'all') params.tipo = filters.tipo;
      if (filters.origem && filters.origem !== 'all') params.origem = filters.origem;

      const response = await api.get(`/partnerships/export/${format}`, {
        params,
        responseType: 'blob' // Importante para download de arquivos
      });

      // Criar URL temporária para download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] 
      });
      const url = window.URL.createObjectURL(blob);
      
      // Extrair nome do arquivo do header Content-Disposition
      const contentDisposition = response.headers['content-disposition'];
      let filename = `parcerias_${new Date().toISOString().split('T')[0]}.${format}`;
      
      if (contentDisposition) {
        // Regex corrigida para capturar apenas o nome sem aspas
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // Criar link temporário e disparar download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Calcula a data final baseada em data_fim + prazo em dias
   */
  static calcularDataFinal(dataFim, prazoDias) {
    if (!dataFim || !prazoDias) return null;
    
    const data = new Date(dataFim);
    data.setDate(data.getDate() + parseInt(prazoDias));
    return data.toISOString().split('T')[0]; // YYYY-MM-DD
  }

  /**
   * Formata data para exibição DD/MM/YYYY
   */
  static formatarData(data) {
    if (!data) return '';
    const d = new Date(data);
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const ano = d.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }
}

// UploadService removido - agora usamos importação via Excel diretamente nas rotas
